import { procedureInputSchema, procedureLabel } from '@iculogbook/shared';
import type { ProcedureInput } from '@iculogbook/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { useAuth } from '../auth/AuthProvider.tsx';
import { useToast } from '../components/index.ts';
import { proceduresRepository } from '../db/repositories/procedures.ts';
import { ProcedureForm } from '../features/log/ProcedureForm.tsx';
import type { FormErrors } from '../features/log/ProcedureForm.tsx';
import { ProcedurePicker } from '../features/log/ProcedurePicker.tsx';
import {
  applyProcedureChange,
  buildProcedureInput,
  initialValues,
  procedureValuesFrom,
} from '../features/log/procedureForm.ts';
import type { ProcedureFormValues } from '../features/log/procedureForm.ts';

/**
 * Logging a standalone procedure: the most important screen in the app.
 *
 * The whole thing is local. The save writes to Dexie and returns — nothing here
 * awaits the network, and there is no spinner tied to one, because this screen
 * has to work in a side room with no signal at four in the morning. The sync
 * engine in phase 12 drains the outbox afterwards, whenever it can.
 */
function LogProcedureRouteForm() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { procedureId } = useParams();
  const location = useLocation();

  const [values, setValues] = useState<ProcedureFormValues | null>(null);
  const [picking, setPicking] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  const userId = user?.id ?? null;
  const editing = procedureId !== undefined;

  /** A duplicate arrives prefilled through the router, never through the URL. */
  const prefill = (location.state as { prefill?: ProcedureInput } | null)?.prefill;

  const stored = useLiveQuery(
    async () =>
      userId === null || procedureId === undefined
        ? null
        : ((await proceduresRepository.get(userId, procedureId)) ?? null),
    [userId, procedureId],
  );

  /*
   * Seed once, then leave the form alone: a live query must never overwrite
   * something half-typed.
   */
  const [seeded, setSeeded] = useState(false);
  if (!seeded && values === null) {
    if (editing) {
      if (stored !== undefined && stored !== null) {
        setSeeded(true);
        setValues(procedureValuesFrom(stored));
        setPicking(false);
      }
    } else if (prefill !== undefined) {
      setSeeded(true);
      setValues(procedureValuesFrom(prefill));
      setPicking(false);
    }
  }

  const recentKeys =
    useLiveQuery(
      async () => (userId === null ? [] : await proceduresRepository.recentlyUsedKeys(userId)),
      [userId],
    ) ?? [];

  function selectProcedure(procedureKey: string): void {
    setValues((current) =>
      current === null
        ? initialValues(procedureKey, new Date())
        : applyProcedureChange(current, procedureKey),
    );
    setErrors({});
    setPicking(false);
  }

  async function save(andDuplicate = false): Promise<void> {
    if (values === null || userId === null) return;

    const input = buildProcedureInput(values);
    if (input === null) {
      setErrors({
        ...(values.ageBand === null ? { ageBand: 'Choose an age band.' } : {}),
        ...(values.capabilityLevel === null
          ? { capabilityLevel: 'Choose a capability level.' }
          : {}),
      });
      return;
    }

    // The shared schema is the only validator. Anything it rejects is a bug in
    // the form rather than something the user did, but it is reported rather
    // than swallowed, because writing an invalid record would be worse.
    const parsed = procedureInputSchema.safeParse(input);
    if (!parsed.success) {
      const collected: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (typeof field === 'string' && collected[field] === undefined) {
          collected[field] = issue.message;
        }
      }
      setErrors(collected);
      return;
    }

    setSaving(true);
    const label = procedureLabel(values.procedureKey);

    try {
      let savedId = procedureId;

      if (editing && procedureId !== undefined) {
        await proceduresRepository.update(userId, procedureId, parsed.data);
        showToast({ message: `${label} updated.`, tone: 'success' });
      } else {
        const saved = await proceduresRepository.create(userId, parsed.data);
        savedId = saved.id;

        showToast({
          message: `${label} saved.`,
          tone: 'success',
          actions: [
            {
              label: 'Undo',
              onAction: () => {
                void proceduresRepository.softDelete(userId, saved.id);
                showToast({ message: `${label} removed.` });
              },
            },
            { label: 'Add another', onAction: () => void navigate('/log/procedure') },
          ],
        });
      }

      const copy =
        andDuplicate && savedId !== undefined
          ? await proceduresRepository.duplicate(userId, savedId)
          : undefined;

      void navigate(copy === undefined ? '/entries' : '/log/procedure', {
        ...(copy === undefined ? {} : { state: { prefill: copy } }),
      });
    } finally {
      setSaving(false);
    }
  }

  if (editing && stored === null) {
    return <p className="text-sm text-muted">That procedure no longer exists.</p>;
  }

  if (values === null || picking) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-sm text-muted">
          Pick a procedure. Recently used ones are at the top; typing searches labels and
          abbreviations.
        </p>
        <ProcedurePicker
          onSelect={selectProcedure}
          recentKeys={recentKeys}
          selectedKey={values?.procedureKey}
          autoFocus
        />
        {values === null ? null : (
          <button
            type="button"
            onClick={() => setPicking(false)}
            className="min-h-touch self-start px-1 text-sm text-accent hover:text-accent-hover"
          >
            Back to {procedureLabel(values.procedureKey)}
          </button>
        )}
      </div>
    );
  }

  return (
    <ProcedureForm
      values={values}
      onChange={setValues}
      onChangeProcedure={() => setPicking(true)}
      onSave={() => void save()}
      onSaveAndDuplicate={() => void save(true)}
      saving={saving}
      errors={errors}
      editing={editing}
    />
  );
}

/**
 * Saving and duplicating navigates to this same route with a fresh prefill.
 * Keying on the location gives that navigation a new instance, so the form
 * seeds from the copy instead of keeping what was typed into the last one.
 */
export function LogProcedureRoute() {
  return <LogProcedureRouteForm key={useLocation().key} />;
}
